// src/App.js

import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Seo from './components/Seo';
import WhatsAppButton from './Pages/WhatsappButton';
import Home from './Pages/Home'; // kept eager so the first page paints fast
import './App.css';

// Lazy-loaded pages: each becomes its own JS chunk, loaded only when visited
const About = lazy(() => import('./Pages/About'));
const Services = lazy(() => import('./Pages/Service'));
const Projects = lazy(() => import('./Pages/Projects'));
const Subsidy = lazy(() => import('./Pages/Subsidy'));
const Gallery = lazy(() => import('./Pages/Gallery'));
const Contact = lazy(() => import('./Pages/Contact'));
const ChatBot = lazy(() => import('./Pages/Component/ChatBot'));

// ---------------------------------------------------------------
// SEO for every page. Edit the text here; no changes needed in the
// individual page files.
// ---------------------------------------------------------------
const SEO = {
  '/': {
    title: 'Revoltant Solar Solutions | Rooftop Solar, Solar Pumps & EMI',
    description:
      'Rooftop, commercial and industrial solar, solar pumps, street lights and cameras with EMI options. Get a free solar quote today.',
  },
  '/about': {
    title: 'About Us | Revoltant Solar Solutions',
    description:
      'Learn about Revoltant Solar Solutions, our team and our approach to solar installation.',
  },
  '/services': {
    title: 'Solar Services: Rooftop, Pumps, Street Lights | Revoltant Solar Solutions',
    description:
      'Rooftop solar, solar water pumps, solar street lights and solar cameras for homes, farms and businesses.',
  },
  '/projects': {
    title: 'Solar Projects | Revoltant Solar Solutions',
    description:
      'See completed residential, commercial and industrial solar installations by Revoltant Solar Solutions.',
  },
  '/subsidy': {
    title: 'PM Surya Ghar Solar Subsidy Help | Revoltant Solar Solutions',
    description:
      'How the PM Surya Ghar rooftop solar subsidy works and how we help you apply.',
  },
  '/gallery': {
    title: 'Solar Installation Gallery | Revoltant Solar Solutions',
    description:
      'Photos of our rooftop solar, solar pump and street light installations.',
  },
  '/contact': {
    title: 'Contact Us | Free Solar Quote | Revoltant Solar Solutions',
    description:
      'Contact Revoltant Solar Solutions on +91 93841 67482 for a free solar quote.',
  },
};

// Reads the current URL and sets the matching title/description/canonical
function RouteSeo() {
  const { pathname } = useLocation();
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const known = SEO[path];

  return <Seo {...(known || SEO['/'])} path={known ? path : '/'} />;
}

const PageLoader = () => (
  <div
    style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Inter, Arial, sans-serif',
      color: '#0b1a2a',
    }}
  >
    Loading...
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <Router>
        <RouteSeo />
        <div className="App">
          <Navbar />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/subsidy" element={<Subsidy />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/contact" element={<Contact />} />
            </Routes>
          </Suspense>
          <Footer />
          <WhatsAppButton />
          <Suspense fallback={null}>
            <ChatBot />
          </Suspense>
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;